import type {
  QueryObserverResult,
  RefetchOptions,
} from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import {
  type FC,
  type JSX,
  type ReactElement,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { IBrandGetById } from "@/api/admin/brands";
import { AdminSections } from "@/api/admin/teamRoles";
import {
  CustomButton,
  CustomCardFooter,
  CustomCardHeader,
  FormCardBasic,
  HeaderWithBack,
  Loader,
} from "@/components/atoms";
import { FormNavigation } from "@/components/molecules";
import { getDefaultFormMenu } from "@/components/units/BrandFormMenu/menuMappers";
import { PATHS } from "@/config/path";
import type { BrandRouter } from "@/modules/BrandStepsAdmin/brandRouter";
import type { DemoBrandStore } from "@/modules/BrandStepsAdmin/DemoBrandStore";
import {
  clearWizardDraft,
  NEW_WIZARD_DRAFT_ENTITY_ID,
} from "@/modules/wizardDraft";
import { usePermission } from "@/services/casl/usePermission";
import type { BrandFormPayload } from "@/store/BrandStore";
import { rootStore } from "@/store/RootStore";
import type { FormRefType } from "@/types/common";

import BrandFormBranches from "../BrandFormBranches";
import type { BranchesFormType } from "../BrandFormBranches/types";
import {
  type BrandContactsType,
  BrandFormContacts,
} from "../BrandFormContacts";
import {
  BrandFormDetails,
  type BrandFormDetailsType,
} from "../BrandFormDetails";
import { BrandFormMenu, type BrandFormMenuType } from "../BrandFormMenu";
import { useBrandNavigation } from "./useBrandNavigation";

interface BrandCreatingProps {
  brandStore: DemoBrandStore;
  router: BrandRouter;
  header?: ReactElement;
  isPending?: boolean;
  editBranchUrl?: string;
  refetch?: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<IBrandGetById, Error>>;
  onSubmit?: () => Promise<boolean | string>;
}

const FIRST_STEP = 1;
const LAST_STEP = 4;

const BrandSteps: FC<BrandCreatingProps> = observer(
  ({ brandStore, router, header, refetch, onSubmit }) => {
    const navigate = useNavigate();
    const { brandId: routeBrandId } = useParams<{ brandId?: string }>();
    const isPending = brandStore.isCreating || brandStore.isUpdating;

    if (!brandStore || !router) {
      throw new Error(
        "BrandSteps requires either BrandWizardContext or props (brandStore, router)"
      );
    }

    const idForm = useId();
    // Track validity per step to avoid conflicts when multiple forms are rendered
    const [stepValidity, setStepValidity] = useState<Record<number, boolean>>({
      1: false,
      2: false,
      3: false,
      4: false,
    });

    const isAdmin = rootStore.authStore.isAdmin;

    const detailsRef = useRef<FormRefType<BrandFormDetailsType>>(null);
    const menuRef = useRef<FormRefType<BrandFormMenuType>>(null);
    const contactsRef = useRef<FormRefType<BrandContactsType>>(null);
    const branchesRef = useRef<FormRefType<BranchesFormType>>(null);

    // Create stable validity handlers for each step using useCallback
    // This prevents infinite loops by ensuring the callbacks don't change on every render
    const handleStep1ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[1] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 1: isValid };
      });
    }, []);

    const handleStep2ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[2] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 2: isValid };
      });
    }, []);

    const handleStep3ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[3] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 3: isValid };
      });
    }, []);

    const handleStep4ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[4] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 4: isValid };
      });
    }, []);

    const canChangeGroup = !brandStore.hasFixedGroupId;
    const isGroupSelectDisabled =
      !canChangeGroup || !!routeBrandId || !!brandStore.details?.id;
    const draftEntityId =
      brandStore.details?.id ?? routeBrandId ?? NEW_WIZARD_DRAFT_ENTITY_ID;

    const clearDraft = (entityId = draftEntityId) => {
      clearWizardDraft("brand", entityId);
    };

    // Render all forms but conditionally show them based on active step
    // This ensures forms maintain their state when switching steps
    const detailsForm = (
      <BrandFormDetails
        ref={detailsRef}
        id={idForm}
        onValidityChange={handleStep1ValidityChange}
        defaultValues={brandStore.details}
        draftEntityId={draftEntityId}
        setValidData={data => {
          brandStore.setAll({ details: data });
        }}
        canChangeGroup={canChangeGroup}
        isGroupSelectDisabled={isGroupSelectDisabled}
        showGroupSelection={true}
        onSubmit={data => {
          handleSubmit({ details: data });
        }}
        classes="px-4 md:px-8"
      />
    );

    const menuForm = (
      <BrandFormMenu
        ref={menuRef}
        id={idForm}
        wizardMode
        onValidityChange={handleStep2ValidityChange}
        defaultValues={brandStore.menu}
        brandId={brandStore.details?.id}
        draftEntityId={draftEntityId}
        onMenuReset={() => {
          brandStore.setMenu(getDefaultFormMenu());
        }}
        onSubmit={data => {
          handleSubmit({ menu: data });
        }}
        classes="px-4 md:px-8"
      />
    );

    const contactsForm = (
      <BrandFormContacts
        ref={contactsRef}
        id={idForm}
        onValidityChange={handleStep3ValidityChange}
        defaultValues={brandStore.contacts}
        draftEntityId={draftEntityId}
        onSubmit={data => {
          handleSubmit({ contacts: data });
        }}
        classes="px-4 md:px-8"
      />
    );

    const branchesForm = (
      <BrandFormBranches
        ref={branchesRef}
        id={idForm}
        onValidityChange={handleStep4ValidityChange}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        defaultValues={brandStore.branchesInBrand.map((b: any) => ({
          branchId: b.branchId,
          branchName: b.branchName,
          active: b.active,
        }))}
        onSubmit={() => {
          // Note: BrandFormBranches returns BranchesFormType which is simplified
          // The branches are managed through the API refetch
          handleSubmit({ branches: brandStore.branches });
        }}
        router={router}
        editBranchUrl={(branchId: string) =>
          PATHS.ADMIN.BRANCH_WIZARD_DETAILS(branchId, brandStore.details?.id)
        }
        refetch={refetch}
        classes="px-4 md:px-8"
      />
    );

    const getStepForm = (stepId: number): JSX.Element => {
      switch (stepId) {
        case 1:
          return detailsForm;
        case 2:
          return menuForm;
        case 3:
          return contactsForm;
        case 4:
          return branchesForm;
        default:
          return <></>;
      }
    };

    const { navigationList, activeId } = useBrandNavigation(
      getStepForm,
      router
    );

    const getCurrentRef = (): React.RefObject<FormRefType<unknown>> => {
      switch (activeId) {
        case 1:
          return detailsRef as React.RefObject<FormRefType<unknown>>;
        case 2:
          return menuRef as React.RefObject<FormRefType<unknown>>;
        case 3:
          return contactsRef as React.RefObject<FormRefType<unknown>>;
        case 4:
          return branchesRef as React.RefObject<FormRefType<unknown>>;
        default:
          return null as unknown as React.RefObject<FormRefType<unknown>>;
      }
    };

    const handleBack = () => {
      if (activeId === FIRST_STEP) {
        clearDraft();
      }
      router.toPrevious();
    };

    const handleSkip = () => {
      const currentRef = getCurrentRef();
      const formData = currentRef?.current?.getValues?.();

      if (formData) {
        const currentStepValid = stepValidity[activeId] ?? false;
        if (currentStepValid) {
          switch (activeId) {
            case 2:
              handleSubmit({ menu: formData as BrandFormMenuType }, true);
              break;
            case 3:
              handleSubmit(
                {
                  contacts: formData as BrandContactsType,
                },
                true
              );
              break;
          }
        } else {
          switch (activeId) {
            case 2:
              brandStore.active = false;
              handleSubmit({ menu: formData as BrandFormMenuType }, true);
              break;
            case 3:
              brandStore.active = false;
              handleSubmit(
                {
                  contacts: formData as BrandContactsType,
                },
                true
              );
              break;
          }
        }
      }
    };

    const hasReadPermission = usePermission(AdminSections.BRAND);
    const isReadOnlyFlow = !hasReadPermission;
    const isSingleStepSave = isReadOnlyFlow && activeId === FIRST_STEP;

    // Wait for initial fetch so forms mount with loaded data (RHF defaultValues are init-only)
    const isInitialLoading = brandStore.isFetching && !brandStore.details?.id;

    const handleSubmit = async (
      data: BrandFormPayload,
      skipValidation = false
    ) => {
      brandStore.setAll(data);

      const currentStepValid = stepValidity[activeId] ?? false;

      // Don't proceed if the current step is invalid
      if (!currentStepValid && !skipValidation) {
        return;
      }

      let submittedEntityId = draftEntityId;
      if (onSubmit) {
        const result = await onSubmit();
        if (!result) {
          return;
        }
        if (typeof result === "string") {
          submittedEntityId = result;
        }
      }

      if (!hasReadPermission) {
        clearDraft(submittedEntityId);
        navigate(`${PATHS.ADMIN.ACCOUNT}#brands`);
        return;
      }

      const finished = activeId === LAST_STEP;
      if (!finished) {
        router.toNext();
      } else {
        clearDraft(submittedEntityId);
        router.close();
      }
    };

    const isMenuStep = activeId === 2;
    const isNextDisabled =
      isPending || (isMenuStep && !(stepValidity[activeId] ?? false));

    if (isInitialLoading) {
      return (
        <FormCardBasic isModal={!isAdmin}>
          <Loader />
        </FormCardBasic>
      );
    }

    return (
      <FormCardBasic isModal={!isAdmin}>
        {header ? (
          <CustomCardHeader classes="flex-row justify-between">
            {header}
          </CustomCardHeader>
        ) : (
          <HeaderWithBack
            linkProps={{
              to: "#",
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                handleBack();
              },
            }}
            title={
              brandStore.details?.nameEn ||
              (brandStore.isNew ? "New brand" : "Edit brand")
            }
          />
        )}

        <FormNavigation navigationList={navigationList} />
        <div
          key={`${activeId}-${brandStore.details?.id ?? "new"}`}
          className="animate-fade-in"
          style={{ display: "block" }}
        >
          {getStepForm(activeId)}
        </div>

        <CustomCardFooter>
          <CustomButton
            type="button"
            variant="light"
            onClick={handleBack}
            data-testid="multi-step-form-back-button"
          >
            {activeId === FIRST_STEP ? "Cancel" : "Back"}
          </CustomButton>

          <div className="flex gap-4">
            {(activeId === 2 || activeId === 3) && (
              <CustomButton
                variant="link"
                type="button"
                onClick={handleSkip}
                data-testid="multi-step-form-skip-button"
              >
                Skip
              </CustomButton>
            )}
            <CustomButton
              type="submit"
              form={idForm}
              isPending={isPending}
              disabled={isNextDisabled}
              data-testid="multi-step-form-next-button"
            >
              {activeId === LAST_STEP || isSingleStepSave ? "Save" : "Next"}
            </CustomButton>
          </div>
        </CustomCardFooter>
      </FormCardBasic>
    );
  }
);

export default BrandSteps;
